import { Currency, PlanRecurrence, PlanTier } from './plans.enums'
import type { PlanSku } from './plans.types'

interface VariantSeed {
  tier: PlanTier
  /** Monthly envelope allowance; -1 = unlimited. */
  volume: number
  /** Cents, monthly SKU. */
  monthlyPrice: number
  /** Cents, yearly SKU (charged once). */
  yearlyPrice: number
  sellsAddOns: boolean
}

// Retail BRL ladder, owner-approved (spec §1). Wholesale mirrors the same
// volumes with different prices, so tests that need it override `price`.
const VARIANTS: Array<VariantSeed> = [
  {
    tier: PlanTier.ONE,
    volume: 30,
    monthlyPrice: 3900,
    yearlyPrice: 27600,
    sellsAddOns: true,
  },
  {
    tier: PlanTier.ONE,
    volume: 100,
    monthlyPrice: 9900,
    yearlyPrice: 70800,
    sellsAddOns: true,
  },
  {
    tier: PlanTier.ONE,
    volume: -1,
    monthlyPrice: 14900,
    yearlyPrice: 106800,
    sellsAddOns: false,
  },
  {
    tier: PlanTier.PRO,
    volume: 30,
    monthlyPrice: 7900,
    yearlyPrice: 56400,
    sellsAddOns: true,
  },
  {
    tier: PlanTier.PRO,
    volume: 100,
    monthlyPrice: 14900,
    yearlyPrice: 106800,
    sellsAddOns: true,
  },
  {
    tier: PlanTier.PRO,
    volume: 200,
    monthlyPrice: 24900,
    yearlyPrice: 178800,
    sellsAddOns: true,
  },
  {
    tier: PlanTier.MAX,
    volume: 200,
    monthlyPrice: 49900,
    yearlyPrice: 358800,
    sellsAddOns: true,
  },
]

// Every retail tier advertises "unlimited users" (pricing-grid bullets.common),
// and the app renders -1 as "Ilimitado" (current-plan-card.tsx:129-130).
// Confirmed against the backend: ListAvailablePlansQueryHandler.cs hardcodes
// `UserSeatsIncluded = -1` as a fixed literal (SIG-2551 Task 6), not a
// per-row seed value, so every SKU carries it regardless of tier.
const UNLIMITED_SEATS = -1

const TIER_LABEL: Record<PlanTier, string> = {
  [PlanTier.ONE]: 'STARTER',
  [PlanTier.PRO]: 'BUSINESS',
  [PlanTier.MAX]: 'ENTERPRISE',
}

const volumeLabel = (volume: number) =>
  volume === -1 ? 'UNLIMITED' : String(volume)

/**
 * Builds a full sellable catalog (both recurrences) shaped exactly like the
 * API response, so every test in the repo asserts against one source of
 * truth instead of a hand-rolled literal.
 */
export function buildPlanCatalog(
  overrides: Partial<PlanSku> = {},
): Array<PlanSku> {
  const skus: Array<PlanSku> = []
  let displayOrder = 0

  for (const variant of VARIANTS) {
    for (const recurrence of [PlanRecurrence.MONTHLY, PlanRecurrence.YEARLY]) {
      const isYearly = recurrence === PlanRecurrence.YEARLY
      const envelopesIncluded =
        variant.volume === -1 ? -1 : variant.volume * (isYearly ? 12 : 1)

      skus.push({
        id: `${TIER_LABEL[variant.tier]}-${volumeLabel(variant.volume)}-${recurrence}`.toLowerCase(),
        // Mirrors the backend plan's SKU code format:
        // RETAIL_<TIER>_<VOLUME>_<INTERVAL>_<CURRENCY>.
        code: `RETAIL_${TIER_LABEL[variant.tier]}_${volumeLabel(variant.volume)}_${recurrence.toUpperCase()}_BRL`,
        tier: variant.tier,
        recurrence,
        currency: Currency.BRL,
        envelopesIncluded,
        price: isYearly ? variant.yearlyPrice : variant.monthlyPrice,
        additionalEnvelopePrice: variant.sellsAddOns ? 100 : null,
        standaloneEnvelopePrice: variant.sellsAddOns ? 200 : null,
        userSeatsIncluded: UNLIMITED_SEATS,
        creditsIncluded: 0,
        creditPrice: null,
        displayOrder: (displayOrder += 1),
        ...overrides,
      })
    }
  }

  return skus
}

export const RETAIL_CATALOG: Array<PlanSku> = buildPlanCatalog()

/** The SKUs of one recurrence, the shape most component tests want. */
export const retailSkus = (recurrence: PlanRecurrence): Array<PlanSku> =>
  RETAIL_CATALOG.filter((sku) => sku.recurrence === recurrence)
